/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux'
import { updateUserProfile, setAppState, showToast } from './actions'
import axios from 'axios';
import { AUTH_CONFIG } from './Auth/auth0-variables';
import Auth from './Auth/Auth';
import Draggable from 'react-draggable';
import ChatBot from './components/Scheduler/Messaging/Chat/ChatBot';
import { FluentProvider, webLightTheme, webDarkTheme } from '@fluentui/react-components';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ConnectedRouter } from 'connected-react-router';
import Routes from './Routes';
import SiteLoading from './Auth/SiteLoading';
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import BugsnagPerformance from '@bugsnag/browser-performance'
import { bootstrapTokenRefresh, clearTokenRefreshTimer, refreshAccessToken, setIdleState, setTokenFailureHandler, isAccessTokenExpiringSoon, REQUEST_REFRESH_BUFFER_MS } from './Auth/tokenManager';

const bugsnagApiKey = process.env.REACT_APP_BUGSNAG_API_KEY;
const IDLE_TIMEOUT_MS = 4 * 60 * 60 * 1000;
Bugsnag.start({
    apiKey: bugsnagApiKey,
    plugins: [new BugsnagPluginReact()],
    onError: function (event) {
        // User info is set later in componentDidMount via Bugsnag.setUser()
        // This function still runs for additional per-error customization if needed
    },
});

BugsnagPerformance.start({ apiKey: bugsnagApiKey });

// React error boundary provided by the Bugsnag React plugin. Reports caught render
// errors to Bugsnag automatically (no manual notify) and renders a fallback UI.
export const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again or refresh the page.';

// --- Azure Application Insights (real user monitoring + distributed tracing to API) ---
// Bugsnag still owns JS errors; App Insights handles page views, route changes,
// AJAX dependency timings, and W3C traceparent correlation with the .NET API.
let appInsights = null;
const aiConnectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;
if (aiConnectionString) {
    appInsights = new ApplicationInsights({
        config: {
            connectionString: aiConnectionString,
            enableAutoRouteTracking: true,           // SPA pushState/replaceState as page views
            enableCorsCorrelation: true,             // send traceparent header to the API
            enableRequestHeaderTracking: true,
            enableResponseHeaderTracking: true,
            distributedTracingMode: 2,               // 2 = W3C traceparent (matches AspNetCore SDK default)
            // Never attach traceparent/Request-Id headers to third-party endpoints.
            // Adding custom headers to a cross-origin GET triggers a CORS preflight
            // (OPTIONS) which Google / Stripe / Auth0 endpoints do not allow, breaking
            // the underlying request (e.g. Google Maps Timezone API).
            correlationHeaderExcludedDomains: [
                '*.googleapis.com',
                '*.google.com',
                '*.gstatic.com',
                '*.stripe.com',
                '*.auth0.com',
                'crisp.chat',
                '*.crisp.chat',
                '*.bugsnag.com',
                '*.cloudflare.com',
                '*.cdnjs.cloudflare.com',
                '*.typekit.net',
                '*.unpkg.com',
                '*.jsdelivr.net'
            ],
            disableExceptionTracking: true,          // Bugsnag is the source of truth for errors
            autoTrackPageVisitTime: true,
        }
    });
    appInsights.loadAppInsights();
    // Make available to other modules (Layout, axios interceptors, etc.) if they ever want to track custom events.
    window.appInsights = appInsights;
}

function logOut() {
    clearTokenRefreshTimer();
    localStorage.removeItem("REDIRECT_ON_LOGIN")
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('refresh_token');
    window.location.href = "/login";
}

setTokenFailureHandler(logOut);
bootstrapTokenRefresh();

axios.interceptors.request.use(async (config) => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const urlToken = urlParams.get('access_token');
    const token = urlToken ? urlToken : localStorage.getItem("access_token");

    const isRefreshCall = config.url && config.url.indexOf("/api/auth/RefreshToken") > -1;

    // Sliding expiration: refresh when the access token is close to expiring and a refresh token exists
    if (!isRefreshCall && localStorage.getItem('refresh_token') && isAccessTokenExpiringSoon(REQUEST_REFRESH_BUFFER_MS)) {
        try {
            const newToken = await refreshAccessToken();
            if (newToken) {
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${newToken}`;
                return config;
            }
        } catch (err) {
            logOut();
            return Promise.reject(err);
        }
    }

    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    } else if (config.headers && config.headers.Authorization) {
        delete config.headers.Authorization;
    }

    return config;
});

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Prevent infinite loop
        if (
            error.response &&
            error.response.status === 401 &&
            !originalRequest._retry &&
            !originalRequest.url.includes("/api/auth/RefreshToken")
        ) {
            try {
                if (!localStorage.getItem('refresh_token')) {
                    logOut();
                    return Promise.reject(error);
                }
                originalRequest._retry = true;
                const newToken = await refreshAccessToken();
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = 'Bearer ' + newToken;
                return axios(originalRequest);
            } catch (refreshError) {
                logOut();
                return Promise.reject(refreshError);
            }
        }

        // Surface a generic error toast for any non-401 failure (network/4xx/5xx).
        // Never echo the server response to avoid exposing sensitive data.
        // Callers can opt out by setting `suppressGlobalError: true` on the request config.
        const status = error.response && error.response.status;
        if (status !== 401 && !(originalRequest && originalRequest.suppressGlobalError)) {
            try {
                window.store.dispatch(showToast({ show: true, type: 'danger', message: GENERIC_ERROR_MESSAGE }));
            } catch (e) {
                // store not ready yet; ignore
            }
        }

        return Promise.reject(error);
    }
);



const isLoggedOutRoute = () => {
    if (window.location.pathname === "/"
        || window.location.pathname.indexOf("signup") > -1
        || window.location.pathname.indexOf("help-center") > -1
        || window.location.pathname.indexOf("login") > -1
        || window.location.pathname.indexOf("demo") > -1
        || window.location.pathname.indexOf("logout") > -1
        || window.location.pathname.indexOf("forgot_password") > -1
        || window.location.pathname.indexOf("test") > -1
        || window.location.pathname.indexOf("activate") > -1
        || window.location.pathname.indexOf("reset_password") > -1) {
        return true;
    } else {
        return false;
    }
}

export class Startup extends React.Component {
    constructor(props) {
        super(props);
        this.auth = new Auth(this.props.history);
        let theme = this.auth.isAuthenticated2() ? this.props.theme : 'light';
        this.state = {
            loading: true,
            theme: theme,
            error: true,
            fluentTheme: theme == "dark" ? {
                ...webDarkTheme,
                fontFamilyBase: 'Wfvisualsans, Helvetica Neue, Helvetica, Arial, sans-serif',
                fontWeightSemibold: '500',
                colorBrandBackground: "#1d88da",
                colorBrandForeground2: "#459ee4"
            } : {
                ...webLightTheme,
                fontFamilyBase: 'Wfvisualsans, Helvetica Neue, Helvetica, Arial, sans-serif',
                fontWeightSemibold: '500',
                colorBrandBackground: "#1d88da",
                colorBrandForeground2: "#459ee4"
            }
        };

        this.idleTimerId = null;
        this.activityEvents = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll', 'click'];
        this.handleUserActivity = this.handleUserActivity.bind(this);
        this.handleIdleTimeout = this.handleIdleTimeout.bind(this);
    }

    componentDidMount() {
        this.startIdleTracking();
        if (
            window.location.pathname.indexOf("time-clock") > -1 ||
            window.location.pathname.indexOf("signup") > -1 ||
            window.location.pathname.indexOf("onboarding") > -1
        ) {
            this.setState({ loading: false })
            return;
        }
        //
        //if (window.location.hash.indexOf("access_denied") > -1) {
        //    window.location.href = AUTH_CONFIG.logOutUrl;
        //    return;
        //}

        //if (!this.auth.isAuthenticated()) {
        //    this.setState({ loading: false })

        //    this.props.history.push('/login')
        //    return;
        //}
       

        if (!isLoggedOutRoute() || this.auth.isAuthenticated2()) {

            axios.get(AUTH_CONFIG.API_URL + "/api/Account/GetAppState?facilityId=&url=" + window.location.pathname)
                .then((response) => {
                    this.props.dispatch(updateUserProfile(response.data.userProfileModel))
                    this.props.dispatch(setAppState(response.data.clientViewModel))

                    const userType = response.data.userProfileModel.userType;
                    const onboardingRequiredCompleted = response.data.clientViewModel?.clientSettings?.onboardingRequiredCompleted;
                    const isAdminUser = userType === "1" || userType === "2";
                    const isOnboardingRoute = window.location.pathname.indexOf("/onboarding") === 0;

                    if (isAdminUser && onboardingRequiredCompleted === false && !isOnboardingRoute) {
                        window.location.href = "/onboarding";
                        return;
                    }

                    const userId = response.data.userProfileModel.userId;
                    const username = response.data.userProfileModel.email;

                    Bugsnag.setUser(userId?.toString(), username);

                    // Tag all App Insights telemetry with the authenticated user/client
                    // so we can filter traces, page views, and dependencies per tenant.
                    if (appInsights && userId) {
                        const clientId = response.data.userProfileModel.clientId;
                        appInsights.setAuthenticatedUserContext(
                            String(userId),
                            clientId != null ? String(clientId) : undefined,
                            true
                        );
                    }

                    this.setState({
                        loading: false
                    });
                    if (window.location.pathname === "/scheduler/my-schedule") {
                        const { scheduleModule, formsModule, auditsModule } = response.data.clientViewModel.clientSettings;
              
                        if (!scheduleModule) {
                            if (formsModule) {
                                window.location.href = "/user/forms";
                            } else if (auditsModule) {
                                window.location.href = "/admin/overview/audits";
                            } else {
                                window.location.href = "/scheduler/colleagues";
                            }
                        }
                    }
                });
        } else {
            this.setState({
                loading: false
            });
        }
        
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.theme !== this.props.theme) {
            let theme = this.auth.isAuthenticated() ? nextProps.theme : 'light';

            this.setState({
                theme: theme,
                fluentTheme: theme == "dark" ? {
                    ...webDarkTheme,
                    fontFamilyBase: 'Wfvisualsans, Helvetica Neue, Helvetica, Arial, sans-serif',
                    fontWeightSemibold: '500',
                    colorBrandBackground: "#1d88da",
                    colorBrandForeground2: "#459ee4"
                } : {
                    ...webLightTheme,
                    fontFamilyBase: 'Wfvisualsans, Helvetica Neue, Helvetica, Arial, sans-serif',
                    fontWeightSemibold: '500',
                    colorBrandBackground: "#1d88da",
                    colorBrandForeground2: "#459ee4"
                }
            });
        }
    }

    componentWillUnmount() {
        this.stopIdleTracking();
    }

    refresh = () => {
        window.location.reload();
    }

    startIdleTracking() {
        this.activityEvents.forEach(eventName => {
            window.addEventListener(eventName, this.handleUserActivity, true);
        });
        this.resetIdleTimer();
    }

    stopIdleTracking() {
        this.activityEvents.forEach(eventName => {
            window.removeEventListener(eventName, this.handleUserActivity, true);
        });
        if (this.idleTimerId) {
            clearTimeout(this.idleTimerId);
            this.idleTimerId = null;
        }
    }

    handleUserActivity() {
        if (!localStorage.getItem('access_token')) {
            return;
        }
        setIdleState(false);
        this.resetIdleTimer();
    }

    resetIdleTimer() {
        if (this.idleTimerId) {
            clearTimeout(this.idleTimerId);
        }
        this.idleTimerId = setTimeout(this.handleIdleTimeout, IDLE_TIMEOUT_MS);
    }

    handleIdleTimeout() {
        setIdleState(true);
        logOut();
    }

    render() {
        const isEmployeeUser = this.props.user && this.props.user.userType === "3";
        return this.state.loading
            ? (<SiteLoading />)
            : <FluentProvider className={isEmployeeUser ? 'employee-layout' : ''} id="Container" theme={this.state.fluentTheme} style={{ height: '100%', backgroundColor: this.state.theme === 'light' ? '#f9f9fa' : '#1b2432', letterSpacing: '-0.03em' }}>
                
                <ConnectedRouter history={this.props.history}>
                    <Routes history={this.props.history} />
                </ConnectedRouter>
                
                {!this.props.closeChat &&
                    <Draggable handle=".drag-handle">
                        <div className="chat-bot-container white-to-grey" style={{ position: 'absolute', bottom: '80px', right: '80px', zIndex: 22222, overflow: 'hidden', borderRadius: 8, boxShadow: 'rgba(0, 0, 0, .22) 0px 25.6px 57.6px 0px, rgba(0, 0, 0, .18) 0px 4.8px 14.4px 0px' }}>
                            <ChatBot chatUserId={this.props.chatUserId} />
                        </div>
                    </Draggable>
                }
            </FluentProvider>


    }
}

function mapStateToProps(state) {
    const user = state.userProfile;
    const closeChat = state.rootReducer.closeChat;
    const chatUserId = state.rootReducer.chatUserId;
    const theme = state.rootReducer.theme;
    return {
        user,
        theme,
        closeChat,
        chatUserId,
    }
}

export default connect(mapStateToProps)(Startup);
